// Mock payments module (spec §7–§11, §14, §15): context, limit checks in the
// exact §14.2 order with verbatim messages, FX quotes, MPIN/OTP authorisation,
// idempotent execution, history and the ledger.
import type {
  AuthSession,
  ExecutePaymentInput,
  FxQuote,
  LimitCheckInput,
  LimitCheckResult,
  Payment,
  PaymentApproval,
  PaymentContext,
  PaymentRail,
  RecentUpiPayee,
  TransactionApprovalAudit,
  TransactionApprovalTask,
} from '@/types/phase2.types';
import { formatIndianCurrency } from '@/utils/currency';
import {
  DAILY_LIMIT_BY_PRODUCT,
  IMPS_MAX_RUPEES,
  NEW_DEVICE_CAP_RUPEES,
  NEW_PAYEE_CAP_RUPEES,
  RAIL_LABELS,
  RTGS_MIN_RUPEES,
  UPI_DAILY_CAP_RUPEES,
  formatWholeRupees,
} from '@/utils/payments';
import { FX_RATES_PAISE, INTERNATIONAL_CHARGE_RUPEES } from '../data';
import { currentCustomerKey, loadDb, saveDb, toPaise, toRupees, type MockDb } from '../db';
import { formatShort, formatTimeOnly, mockNow, mockToday } from '../clock';
import { fail, mockId, pause, paymentReference } from '../util';

// ---------- context ----------

/**
 * Returns the payment context. On the very first call the mock balance may be
 * seeded from the real selected account so web screens agree with the
 * dashboard; after that the ledger drives the balance (§15).
 */
export const getPaymentContext = async (
  seed?: { balanceRupees?: number; productType?: PaymentContext['productType'] }
): Promise<PaymentContext> => {
  await pause(250);
  const db = loadDb();
  if (!db.seeded) {
    db.seeded = true;
    db.balancePaise = toPaise(seed?.balanceRupees ?? 0);
  }
  if (seed?.productType) db.productType = seed.productType;
  saveDb(db);
  settleDueNefts(db);
  const productType = db.productType;
  return {
    balanceRupees: toRupees(db.balancePaise),
    productType,
    dailyLimitRupees: DAILY_LIMIT_BY_PRODUCT[productType] ?? DAILY_LIMIT_BY_PRODUCT.SAVINGS,
    dailyUsedRupees: toRupees(db.limits.dailyUsedPaise),
    upiDailyCapRupees: UPI_DAILY_CAP_RUPEES,
    upiUsedRupees: toRupees(db.limits.upiUsedPaise),
    upiEnabled: db.limits.upiEnabled,
    newDeviceUntil: db.limits.removeTimeLimits ? undefined : db.limits.newDeviceUntil,
  };
};

// ---------- limit checks (§14.2, in order) ----------

export const checkLimits = (db: MockDb, input: LimitCheckInput): LimitCheckResult => {
  const now = mockNow(db);
  const limits = db.limits;
  const amountPaise = toPaise(input.amountRupees);
  const chargesPaise = toPaise(input.chargesRupees ?? 0);

  // 1. Debits frozen
  if (limits.debitsFrozen) return { ok: false, message: 'Debits are frozen on this account.' };
  // 2. Account active and mobile banking set up
  if (!limits.mobileBanking) return { ok: false, message: 'Account not active.' };
  // 3. UPI switched on (UPI only)
  if (input.rail === 'UPI' && !limits.upiEnabled) {
    return {
      ok: false,
      message: 'UPI is not activated on this account. Switch it on in Profile › App Settings.',
    };
  }
  // 4. Amount above zero
  if (amountPaise <= 0) return { ok: false, message: 'Invalid Amount. Please enter valid amount.' };
  // 5. Balance covers amount plus charges
  if (db.balancePaise < amountPaise + chargesPaise) {
    return {
      ok: false,
      message: `Insufficient balance. Available: ${formatIndianCurrency(toRupees(db.balancePaise))}.`,
    };
  }
  // 6. RTGS minimum
  if (input.rail === 'RTGS' && input.amountRupees < RTGS_MIN_RUPEES) {
    return { ok: false, message: 'RTGS needs ₹2,00,000 or more.' };
  }
  // 7. IMPS maximum
  if (input.rail === 'IMPS' && input.amountRupees > IMPS_MAX_RUPEES) {
    return { ok: false, message: 'IMPS allows up to ₹5,00,000 per transfer.' };
  }
  // 8. Daily limit
  const dailyLimitPaise = toPaise(
    DAILY_LIMIT_BY_PRODUCT[db.productType] ?? DAILY_LIMIT_BY_PRODUCT.SAVINGS
  );
  const remaining = dailyLimitPaise - limits.dailyUsedPaise;
  if (amountPaise > remaining) {
    return {
      ok: false,
      message: `Daily limit ${formatWholeRupees(toRupees(dailyLimitPaise))} reached. Remaining today: ${formatWholeRupees(Math.max(0, toRupees(remaining)))}.`,
    };
  }
  // 9. UPI daily cap
  if (input.rail === 'UPI' && amountPaise > toPaise(UPI_DAILY_CAP_RUPEES) - limits.upiUsedPaise) {
    return { ok: false, message: 'UPI daily limit reached.' };
  }
  if (limits.removeTimeLimits || limits.skipCooling) return approvalCheck(input);

  const account = input.account;
  // 10. New payee still activating (never UPI — §17.6)
  if (account && account.kind !== 'MOBILE_TRANSFER' && !limits.skipCooling) {
    const { activeAt, limitedUntil } = account.status;
    if (!limits.removeTimeLimits && activeAt && now < activeAt) {
      return {
        ok: false,
        message: `You can pay this beneficiary from ${formatTimeOnly(activeAt)}.`,
      };
    }
    // 11. New payee's first-24-hours cap (local transfers only — §14.1)
    if (account.kind === 'LOCAL_TRANSFER' && limitedUntil && now < limitedUntil) {
      if (input.amountRupees > NEW_PAYEE_CAP_RUPEES) {
        return {
          ok: false,
          message: `This payee is limited to ${formatWholeRupees(NEW_PAYEE_CAP_RUPEES)} until ${formatShort(limitedUntil)}.`,
        };
      }
    }
  }
  // 12. New-device cap
  if (!limits.removeTimeLimits && limits.newDeviceUntil && now < limits.newDeviceUntil) {
    if (input.amountRupees > NEW_DEVICE_CAP_RUPEES) {
      return {
        ok: false,
        message: `New device: limited to ${formatWholeRupees(NEW_DEVICE_CAP_RUPEES)} until ${formatShort(limits.newDeviceUntil)}.`,
      };
    }
  }
  return approvalCheck(input);
};

const approvalCheck = (input: LimitCheckInput): LimitCheckResult => {
  const approvalRequired = input.rail !== 'UPI' && input.amountRupees >= 10_000;
  return { ok: true, approvalRequired, approvalLevels: approvalRequired ? (input.amountRupees >= 25_000 ? 2 : 1) : 0 };
};

export const checkLimitsApi = async (input: LimitCheckInput): Promise<LimitCheckResult> => {
  await pause(250);
  const db = loadDb();
  settleDueNefts(db);
  return checkLimits(db, input);
};

// ---------- FX ----------

export const getFxQuote = async (currency: string): Promise<FxQuote> => {
  await pause(400);
  const ratePaise = FX_RATES_PAISE[currency];
  if (!ratePaise) fail('FX_UNAVAILABLE', `No rate available for ${currency}.`);
  return { currency, ratePaise, chargesRupees: INTERNATIONAL_CHARGE_RUPEES };
};

// ---------- authorisation (§9) ----------

interface PaymentAuth {
  authId: string;
  approvalLine: string;
  mpinVerified: boolean;
  ticket?: string;
}

const paymentAuths = new Map<string, PaymentAuth>();

const LOCK_MESSAGE = 'Too many attempts. Your MPIN is locked. Use Forgot MPIN in App Settings.';

export const beginPaymentAuth = async (approvalLine: string): Promise<AuthSession> => {
  await pause(200);
  const db = loadDb();
  const now = mockNow(db);
  if (!db.limits.removeTimeLimits) {
    if (db.limits.paymentsLockedUntil && now < db.limits.paymentsLockedUntil) {
      fail('PAYMENTS_LOCKED', 'Too many attempts. Payments are locked for 30 minutes.');
    }
    if (db.limits.mpinLockedUntil && now < db.limits.mpinLockedUntil) {
      fail('PIN_LOCKED', LOCK_MESSAGE);
    }
  }
  const auth: PaymentAuth = { authId: mockId('payauth'), approvalLine, mpinVerified: false };
  paymentAuths.set(auth.authId, auth);
  return { authId: auth.authId, approvalLine, expiresInSeconds: 0 };
};

export const verifyPaymentMpin = async (
  authId: string,
  mpin: string
): Promise<{ authTicket: string }> => {
  await pause(350);
  const db = loadDb();
  const auth = paymentAuths.get(authId) ?? fail('AUTH_EXPIRED', 'The session has expired. Please try again.');
  const now = mockNow(db);
  if (!db.limits.removeTimeLimits && db.limits.mpinLockedUntil && now < db.limits.mpinLockedUntil) {
    fail('PIN_LOCKED', LOCK_MESSAGE);
  }
  if (mpin !== db.limits.mpin) {
    db.limits.mpinFailures += 1;
    if (db.limits.mpinFailures >= 3) {
      db.limits.mpinLockedUntil = now + 24 * 3_600_000;
      db.limits.mpinFailures = 0;
      saveDb(db);
      fail('PIN_LOCKED', LOCK_MESSAGE);
    }
    saveDb(db);
    fail('MPIN_INCORRECT', 'Incorrect MPIN.');
  }
  db.limits.mpinFailures = 0;
  saveDb(db);
  auth.mpinVerified = true;
  auth.ticket = mockId('ticket');
  // MPIN alone authorizes non-UPI payments in Phase 5. No OTP follows it.
  return { authTicket: `${authId}::${auth.ticket}` };
};

export const verifyPaymentOtp = async (authId: string, code: string): Promise<{ authTicket: string }> => {
  void authId;
  void code;
  return fail('OTP_NOT_REQUIRED', 'OTP is not required after MPIN.');
};

export const resendPaymentOtp = async (authId: string): Promise<{ expiresInSeconds: number }> => {
  void authId;
  return fail('OTP_NOT_REQUIRED', 'OTP is not required after MPIN.');
};

const consumeTicket = (authId: string, ticket: string): void => {
  const auth = paymentAuths.get(authId) ?? fail('AUTH_EXPIRED', 'Authorisation has expired. Please try again.');
  if (auth.ticket !== ticket) fail('AUTH_EXPIRED', 'Authorisation has expired. Please try again.');
  paymentAuths.delete(authId);
};

/** UPI PIN verification produces a standalone ticket (no MPIN/OTP session). */
export const issueUpiTicket = (): string => mockId('upi_ticket');
const upiTickets = new Set<string>();
export const registerUpiTicket = (ticket: string): void => void upiTickets.add(ticket);
export const consumeUpiTicket = (ticket: string): boolean => upiTickets.delete(ticket);

// ---------- maker-checker approval workflow ----------

const APPROVAL_QUEUE_KEY = 'nova_maker_checker_queue';
const makerId = (): string => localStorage.getItem('customerId') || 'maker-demo';
const checkerId = (): string => localStorage.getItem('nova_mock_checker_id') || 'checker-demo';
const checkerOrganization = (): string => localStorage.getItem('nova_mock_checker_org') || 'NOVA';
const checkerRole = (): string => localStorage.getItem('nova_mock_checker_role') || 'CHECKER';
const checkerLimitPaise = (): number => Number(localStorage.getItem('nova_mock_checker_limit_paise') || 500_000_00);

const loadApprovalQueue = (): TransactionApprovalTask[] => {
  try {
    const raw = localStorage.getItem(APPROVAL_QUEUE_KEY);
    return raw ? (JSON.parse(raw) as TransactionApprovalTask[]) : [];
  } catch {
    return [];
  }
};

const saveApprovalQueue = (queue: TransactionApprovalTask[]): void => {
  localStorage.setItem(APPROVAL_QUEUE_KEY, JSON.stringify(queue));
};

const audit = (
  action: TransactionApprovalAudit['action'],
  actorId: string,
  actorRole: TransactionApprovalAudit['actorRole'],
  reason?: string,
): TransactionApprovalAudit => ({
  id: mockId('audit'),
  action,
  actorId,
  actorRole,
  reason,
  createdAt: mockNow(loadDb()),
});

const approvalPayment = (input: ExecutePaymentInput, db: MockDb, taskId: string): Payment => ({
  id: mockId('pay'),
  reference: paymentReference(new Date(mockNow(db)), db.paymentCounter + 1),
  idempotencyKey: input.idempotencyKey,
  rail: input.rail,
  amountRupees: input.amountRupees,
  chargesRupees: input.fx?.chargesRupees ?? 0,
  status: 'PENDING_APPROVAL',
  levelsRequired: input.amountRupees >= 25_000 ? 2 : 1,
  currentLevel: 0,
  payeeName: input.payeeName,
  beneficiaryId: input.beneficiaryId,
  beneficiaryAlias: input.beneficiaryAlias,
  accountId: input.accountId,
  destination: input.destination,
  purpose: input.purpose,
  remark: input.remark,
  fx: input.fx
    ? { currency: input.fx.currency, ratePaise: input.fx.ratePaise, theyReceive: Math.round((toPaise(input.amountRupees) / input.fx.ratePaise) * 100) / 100 }
    : undefined,
  createdAt: mockNow(db),
  approval: {
    transactionId: taskId,
    makerId: makerId(),
    makerCustomerKey: currentCustomerKey(),
    organizationId: 'NOVA',
    transactionType: input.rail,
    amountPaise: toPaise(input.amountRupees),
    levelsRequired: input.amountRupees >= 25_000 ? 2 : 1,
    currentLevel: 0,
    status: 'PENDING_APPROVAL',
    submittedAt: mockNow(db),
    auditLog: [audit('SUBMITTED', makerId(), 'MAKER')],
  },
});

export const submitPaymentForApproval = async (input: ExecutePaymentInput): Promise<Payment> => {
  await pause(350);
  const db = loadDb();
  const existing = db.payments.find((payment) => payment.idempotencyKey === input.idempotencyKey);
  if (existing) return existing;
  if (!consumeUpiTicket(input.authTicket)) {
    const [authId, ticket] = input.authTicket.split('::');
    consumeTicket(authId, ticket);
  }
  const taskId = mockId('approval');
  const payment = approvalPayment(input, db, taskId);
  db.paymentCounter += 1;
  db.payments.unshift(payment);
  db.approvalTasks.unshift(payment.approval as TransactionApprovalTask);
  const queue = loadApprovalQueue();
  queue.unshift(payment.approval as TransactionApprovalTask);
  saveApprovalQueue(queue);
  saveDb(db);
  return payment;
};

const approvalDetails = (payment: Payment): PaymentApproval => ({
  approvalId: payment.approval?.transactionId ?? payment.approvalId ?? payment.id,
  reference: payment.reference,
  status: payment.status === 'SUCCESS' || payment.status === 'APPROVED' ? 'EXECUTED' : payment.status === 'PENDING_APPROVAL' || payment.status === 'REJECTED' || payment.status === 'CANCELLED' || payment.status === 'FAILED' ? payment.status : 'FAILED',
  amountPaise: toPaise(payment.amountRupees),
  rail: payment.rail,
  debitAccountNumber: '',
  beneficiaryName: payment.payeeName,
  beneficiaryAccountNumber: payment.destination,
  beneficiaryIfsc: '',
  remarks: payment.remark,
  levelsRequired: payment.levelsRequired ?? 1,
  currentLevel: payment.currentLevel ?? 0,
  createdAt: payment.createdAt,
  decidedAt: payment.approval?.decidedAt,
  rejectionReason: payment.rejectionReason ?? payment.approval?.rejectionReason,
  cancelledAt: payment.cancelledAt,
  canCancel: payment.status === 'PENDING_APPROVAL' && payment.approval?.makerCustomerKey === currentCustomerKey(),
  message: payment.approvalMessage ?? (payment.status === 'PENDING_APPROVAL' ? 'Awaiting bank approval.' : ''),
});

export const listPaymentApprovals = async (status?: PaymentApproval['status']): Promise<PaymentApproval[]> => {
  await pause(200);
  const db = loadDb();
  return db.payments
    .filter((payment) => payment.approval)
    .map(approvalDetails)
    .filter((approval) => !status || approval.status === status);
};

export const getPaymentApproval = async (approvalId: string): Promise<PaymentApproval> => {
  await pause(200);
  const db = loadDb();
  const foundPayment = db.payments.find((item) => item.approval?.transactionId === approvalId || item.approvalId === approvalId || item.id === approvalId) ?? fail('APPR_001', 'Approval request not found.');
  return approvalDetails(foundPayment);
};

export const cancelPaymentApproval = async (approvalId: string): Promise<PaymentApproval> => {
  await pause(250);
  const db = loadDb();
  const foundPayment = db.payments.find((item) => item.approval?.transactionId === approvalId || item.approvalId === approvalId || item.id === approvalId) ?? fail('APPR_001', 'Approval request not found.');
  if (foundPayment.approval?.makerCustomerKey !== currentCustomerKey()) fail('APPR_001', 'Approval request not found.');
  if (foundPayment.status !== 'PENDING_APPROVAL') fail('APPR_002', 'This approval request is no longer pending.');
  foundPayment.status = 'CANCELLED';
  foundPayment.cancelledAt = mockNow(db);
  if (foundPayment.approval) {
    foundPayment.approval.status = 'CANCELLED';
    foundPayment.approval.decidedAt = foundPayment.cancelledAt;
    foundPayment.approval.auditLog.push(audit('CANCELLED', makerId(), 'MAKER'));
  }
  const task = db.approvalTasks.find((item) => item.transactionId === approvalId);
  if (task) Object.assign(task, foundPayment.approval);
  const queue = loadApprovalQueue();
  const queued = queue.find((item) => item.transactionId === approvalId);
  if (queued && foundPayment.approval) Object.assign(queued, foundPayment.approval);
  saveApprovalQueue(queue);
  saveDb(db);
  return approvalDetails(foundPayment);
};

export const listApprovalTasks = async (): Promise<TransactionApprovalTask[]> => {
  await pause(200);
  return loadApprovalQueue().filter((task) => task.status === 'PENDING_APPROVAL');
};

export const claimApprovalTask = async (transactionId: string, actor = checkerId()): Promise<TransactionApprovalTask> => {
  const db = loadDb();
  const queue = loadApprovalQueue();
  const task = queue.find((item) => item.transactionId === transactionId) ?? fail('NOT_FOUND', 'Approval task not found.');
  if (task.status !== 'PENDING_APPROVAL') fail('APPROVAL_STATE', 'This transaction is no longer awaiting approval.');
  if (actor === task.makerId) fail('APPROVAL_SELF', 'The maker cannot approve their own transaction.');
  if (checkerRole() !== 'CHECKER') fail('APPROVAL_ROLE', 'Checker permission is required.');
  if (task.assignedCheckerId && task.assignedCheckerId !== actor) fail('APPROVAL_ASSIGNED', 'This transaction is assigned to another checker.');
  task.assignedCheckerId = actor;
  task.auditLog.push(audit('CLAIMED', actor, 'CHECKER'));
  const makerTask = db.approvalTasks.find((item) => item.transactionId === transactionId);
  if (makerTask) Object.assign(makerTask, task);
  saveApprovalQueue(queue);
  saveDb(db);
  return task;
};

const updateApproval = async (transactionId: string, decision: 'APPROVED' | 'REJECTED', reason?: string): Promise<Payment> => {
  const queue = loadApprovalQueue();
  const task = queue.find((item) => item.transactionId === transactionId) ?? fail('NOT_FOUND', 'Approval task not found.');
  const actor = checkerId();
  if (actor === task.makerId) fail('APPROVAL_SELF', 'The maker cannot approve their own transaction.');
  if (checkerRole() !== 'CHECKER') fail('APPROVAL_ROLE', 'Checker permission is required.');
  if (task.assignedCheckerId && task.assignedCheckerId !== actor) fail('APPROVAL_ASSIGNED', 'This transaction is assigned to another checker.');
  if (checkerOrganization() !== task.organizationId) fail('APPROVAL_ORGANIZATION', 'Checker is not authorized for this organization.');
  if (task.amountPaise > checkerLimitPaise()) fail('APPROVAL_LIMIT', 'Checker approval limit is too low for this transaction.');
  if (decision === 'REJECTED' && !reason?.trim()) fail('APPROVAL_REASON', 'A rejection reason is required.');
  const makerDb = loadDb(task.makerCustomerKey ?? currentCustomerKey());
  const payment = makerDb.payments.find((item) => item.approval?.transactionId === transactionId) ?? fail('NOT_FOUND', 'Transaction not found.');
  task.assignedCheckerId = task.assignedCheckerId || actor;
  task.rejectionReason = reason?.trim();
  task.decidedAt = mockNow(makerDb);
  task.auditLog.push(audit(decision, actor, 'CHECKER', reason?.trim()));
  payment.approval = task;
  const makerTask = makerDb.approvalTasks.find((item) => item.transactionId === transactionId);
  const requiredLevels = task.levelsRequired ?? 1;
  const nextLevel = (task.currentLevel ?? 0) + 1;
  if (decision === 'REJECTED') {
    task.status = 'REJECTED';
    payment.status = 'REJECTED';
  } else if (nextLevel < requiredLevels) {
    task.status = 'PENDING_APPROVAL';
    task.currentLevel = nextLevel;
    payment.currentLevel = nextLevel;
    payment.status = 'PENDING_APPROVAL';
  } else {
    task.status = 'COMPLETED';
    task.currentLevel = requiredLevels;
    payment.currentLevel = requiredLevels;
    payment.status = 'APPROVED';
  }
  if (decision === 'APPROVED' && task.status === 'COMPLETED') {
    const amountPaise = toPaise(payment.amountRupees);
    const chargesPaise = toPaise(payment.chargesRupees);
    if (makerDb.balancePaise < amountPaise + chargesPaise) fail('LIMIT', 'Insufficient balance.');
    makerDb.balancePaise -= amountPaise + chargesPaise;
    rollDailyTotals(makerDb);
    makerDb.limits.dailyUsedPaise += amountPaise;
    if (payment.rail === 'UPI') makerDb.limits.upiUsedPaise += amountPaise;
    payment.status = 'SUCCESS';
    makerDb.ledger.unshift({ id: mockId('txn'), date: mockNow(makerDb), description: `Sent to ${payment.payeeName}`, counterparty: payment.payeeName, amountRupees: -(payment.amountRupees + payment.chargesRupees), category: RAIL_LABELS[payment.rail], reference: payment.reference, paymentId: payment.id });
  }
  if (makerTask) Object.assign(makerTask, task);
  saveApprovalQueue(queue);
  saveDb(makerDb);
  return payment;
};

export const approveApprovalTask = (transactionId: string): Promise<Payment> => updateApproval(transactionId, 'APPROVED');
export const rejectApprovalTask = (transactionId: string, reason: string): Promise<Payment> => updateApproval(transactionId, 'REJECTED', reason);

// ---------- execute (§7.3, §10, §15) ----------

const executeImmediatePayment = async (input: ExecutePaymentInput, db: MockDb): Promise<Payment> => {
  if (!consumeUpiTicket(input.authTicket)) {
    const [authId, ticket] = input.authTicket.split('::');
    consumeTicket(authId, ticket);
  }

  const forced = db.limits.nextOutcome;
  db.limits.nextOutcome = undefined;
  const now = mockNow(db);
  db.paymentCounter += 1;
  const reference = paymentReference(new Date(now), db.paymentCounter);
  let status: Payment['status'] = 'SUCCESS';
  let settleAt: number | undefined;
  if (forced === 'FAILED') status = 'FAILED';
  else if (forced === 'PENDING') status = 'PENDING';
  else if (input.rail === 'NEFT') {
    status = 'PENDING';
    settleAt = now + 5_000;
  } else if (input.rail === 'INTERNATIONAL') {
    status = 'PENDING';
  }

  const amountPaise = toPaise(input.amountRupees);
  const chargesPaise = toPaise(input.fx?.chargesRupees ?? 0);
  const payment: Payment = {
    id: mockId('pay'),
    reference,
    idempotencyKey: input.idempotencyKey,
    rail: input.rail,
    amountRupees: input.amountRupees,
    chargesRupees: input.fx?.chargesRupees ?? 0,
    status,
    failureReason: status === 'FAILED' ? 'The bank could not process this payment.' : undefined,
    payeeName: input.payeeName,
    beneficiaryId: input.beneficiaryId,
    beneficiaryAlias: input.beneficiaryAlias,
    accountId: input.accountId,
    destination: input.destination,
    purpose: input.purpose,
    remark: input.remark,
    fx: input.fx
      ? { currency: input.fx.currency, ratePaise: input.fx.ratePaise, theyReceive: Math.round((amountPaise / input.fx.ratePaise) * 100) / 100 }
      : undefined,
    createdAt: now,
    settleAt,
  };

  if (status !== 'FAILED') {
    db.balancePaise -= amountPaise + chargesPaise;
    rollDailyTotals(db);
    db.limits.dailyUsedPaise += amountPaise;
    if (input.rail === 'UPI') db.limits.upiUsedPaise += amountPaise;
    db.ledger.unshift({
      id: mockId('txn'), date: now, description: `Sent to ${input.payeeName}`, counterparty: input.payeeName,
      amountRupees: -(input.amountRupees + (input.fx?.chargesRupees ?? 0)), category: RAIL_LABELS[input.rail], reference, paymentId: payment.id,
    });
  }
  db.payments.unshift(payment);
  saveDb(db);
  return payment;
};

export const executePayment = async (input: ExecutePaymentInput): Promise<Payment> => {
  await pause(700);
  const db = loadDb();
  const existing = db.payments.find((payment) => payment.idempotencyKey === input.idempotencyKey);
  if (existing) return existing;

  const check = checkLimits(db, {
    rail: input.rail,
    amountRupees: input.amountRupees,
    chargesRupees: input.fx?.chargesRupees ?? 0,
  });
  if (!check.ok) fail('LIMIT', check.message);
  return check.approvalRequired ? submitPaymentForApproval(input) : executeImmediatePayment(input, db);
};

// ---------- history (§11) ----------

const rollDailyTotals = (db: MockDb): void => {
  const today = mockToday(db);
  if (db.limits.dailyDate !== today) {
    db.limits.dailyDate = today;
    db.limits.dailyUsedPaise = 0;
    db.limits.upiUsedPaise = 0;
  }
};

/** Settles NEFT payments whose 5 seconds have passed (§11.1, §15). */
const settleDueNefts = (db: MockDb): boolean => {
  const now = mockNow(db);
  let changed = false;
  for (const p of db.payments) {
    if (p.status === 'PENDING' && p.settleAt && now >= p.settleAt) {
      p.status = 'SUCCESS';
      p.settleAt = undefined;
      changed = true;
    }
  }
  if (changed) saveDb(db);
  return changed;
};

export const listPayments = async (): Promise<Payment[]> => {
  await pause(400);
  const db = loadDb();
  settleDueNefts(db);
  rollDailyTotals(db);
  saveDb(db);
  return [...db.payments];
};

export const getRecentUpiPayees = async (): Promise<RecentUpiPayee[]> => {
  await pause(250);
  const db = loadDb();
  settleDueNefts(db);
  const seen = new Set<string>();
  const payees: RecentUpiPayee[] = [];
  for (const p of db.payments) {
    if (p.rail !== 'UPI' || p.status === 'FAILED' || p.beneficiaryId) continue;
    if (seen.has(p.destination)) continue;
    seen.add(p.destination);
    const mobile = p.destination.replace(/^\+91\s?/, '');
    payees.push({
      name: p.payeeName,
      upiId: p.destination.includes('@') ? p.destination : undefined,
      mobile: p.destination.includes('@') ? undefined : mobile,
      lastPaidAt: p.createdAt,
    });
    if (payees.length >= 8) break;
  }
  return payees;
};

// ---------- test-panel hooks used by other modules ----------

export const settlePendingPayments = async (): Promise<number> => {
  await pause(300);
  const db = loadDb();
  let count = 0;
  for (const p of db.payments) {
    if (p.status === 'PENDING') {
      p.status = 'SUCCESS';
      p.settleAt = undefined;
      count += 1;
    }
  }
  saveDb(db);
  return count;
};

export const addIncomingCredit = async (input: {
  amountRupees: number;
  sender: string;
  mode: string;
}): Promise<void> => {
  await pause(400);
  const db = loadDb();
  const now = mockNow(db);
  db.seeded = true;
  db.balancePaise += toPaise(input.amountRupees);
  db.ledger.unshift({
    id: mockId('txn'),
    date: now,
    description: `Received from ${input.sender}`,
    counterparty: input.sender,
    amountRupees: input.amountRupees,
    category: input.mode,
  });
  saveDb(db);
};

export type { PaymentRail };
